import { formatColor } from '@meitu/saas-colors';
import { trackConfig, type IFramework } from '@/framework';
import { LayerType, VectorType, type ILayerProps, type ILinearGradient, type IVectorLayerProps } from '@/version-types';
import { pushHistory, requestVectorSVGPreview, updateLayer } from '../utils';

export interface IStroke {
    /**
     * 添加描边
     *
     * @param layerID - 图层 ID
     * @param config - 配置；`config.color` - 描边默认颜色；`config.width` - 描边默认宽度
     * @returns 更新后的图层
     */
    addStroke(layerID: string, config: { color: string; width?: number }): ILayerProps | void;

    /**
     * 设置描边可见性
     *
     * @param layerID - 图层 ID
     * @param flag - 可见性
     * @returns 更新后的图层
     */
    setStrokeVisibility(layerID: string, flag: boolean): ILayerProps | void;

    /**
     * 移除描边
     *
     * @param layerID - 图层 ID
     * @returns 更新后的图层
     */
    removeStroke(layerID: string): ILayerProps | void;

    /**
     * 设置描边样式
     *
     * @param layerID - 图层 ID
     * @param config - 配置；`config.dasharray` - 虚线样式；`config.linecap` - 线条末端样式
     * @returns 更新后的图层
     */
    setStrokeStyle(layerID: string, config: { dasharray: string; linecap: string }): ILayerProps | void;

    /**
     * 设置描边宽度
     *
     * @param layerID - 图层 ID
     * @param width - 宽度
     * @param options - 可选；配置项；`options.writeHistory` - 是否写入历史记录
     * @returns 更新后的图层
     */
    setStrokeWidth(layerID: string, width: number, options?: { writeHistory: boolean }): ILayerProps | void;

    /**
     * 设置描边颜色
     *
     * @param layerID - 图层 ID
     * @param color - 颜色
     * @param options - 配置项
     * @param options - 可选；配置项；`options.writeHistory` - 是否写入历史记录
     * @returns 更新后的图层
     */
    setStrokeColor(layerID: string, color: string, options?: { writeHistory: boolean }): ILayerProps | void;
}

export class Stroke implements IStroke {
    private prevLayer: ILayerProps | null = null;

    constructor(private readonly framework: IFramework) {}

    addStroke(layerID: string, config: { color: string; width?: number }) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType === VectorType.Graph) return;

        const { color, width } = config;
        const isGradient = color.startsWith('linear-gradient');

        const nextLayerData: IVectorLayerProps = {
            ...prevLayerData,
            stroke: isGradient ? '' : color,
            strokeGradient: isGradient ? (formatColor(color) as ILinearGradient) : '',
        };

        if (width) nextLayerData.strokeWidth = width;

        const nextLayer: ILayerProps = {
            ...prevLayer,
            data: nextLayerData,
        };

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER].stroke);

        return nextLayer;
    }

    setStrokeVisibility(layerID: string, flag: boolean) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType === VectorType.Graph) return;

        const nextLayerData: IVectorLayerProps = { ...prevLayerData, strokeHidden: flag };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER].stroke);

        return nextLayer;
    }

    removeStroke(layerID: string) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType === VectorType.Graph) return;

        const nextLayerData: IVectorLayerProps = {
            ...prevLayerData,
            stroke: '',
            strokeWidth: 0,
            strokeHidden: false,
            strokeGradient: null,
            strokeDasharray: '',
            strokeLinecap: '',
        };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER].stroke);

        return nextLayer;
    }

    setStrokeStyle(layerID: string, config: { dasharray: string; linecap: string }) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType === VectorType.Graph) return;

        const { dasharray, linecap } = config;
        const nextLayerData: IVectorLayerProps = {
            ...prevLayerData,
            strokeDasharray: dasharray,
            strokeLinecap: linecap,
        };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER].strokeDasharray);

        return nextLayer;
    }

    setStrokeWidth(layerID: string, width: number, options = { writeHistory: false }) {
        const prevLayer = (() => {
            if (this.prevLayer?.id === layerID) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType === VectorType.Graph) return;

        const { writeHistory } = options;

        if (writeHistory) {
            const nextLayer = this.framework.service.flatLayer.getLayer(layerID, true);

            requestVectorSVGPreview(this.framework, 'commit', nextLayer);
            updateLayer(this.framework, nextLayer);

            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER].strokeWidth);
            this.prevLayer = null;

            return nextLayer;
        }

        const nextLayerData: IVectorLayerProps = { ...prevLayerData, strokeWidth: width };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        requestVectorSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { dataOnly: true });

        return nextLayer;
    }

    setStrokeColor(layerID: string, color: string, options = { writeHistory: true }) {
        const prevLayer = (() => {
            if (this.prevLayer?.id === layerID) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType === VectorType.Graph) return;

        const { writeHistory } = options;

        if (writeHistory) {
            const nextLayer = this.framework.service.flatLayer.getLayer(layerID, true);

            requestVectorSVGPreview(this.framework, 'commit', nextLayer);
            updateLayer(this.framework, nextLayer);

            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER].stroke);
            this.prevLayer = null;

            return nextLayer;
        }

        const isGradient = color.startsWith('linear-gradient');
        const nextLayerData: IVectorLayerProps = {
            ...prevLayerData,
            stroke: isGradient ? '' : color,
            strokeGradient: isGradient ? (formatColor(color) as ILinearGradient) : '',
        };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        requestVectorSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { dataOnly: true });

        return nextLayer;
    }
}
